import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { DateSelectArg, EventClickArg } from '@fullcalendar/core';

import { finalize, take, takeUntil } from 'rxjs';

import {
  COMPONENT_MESSAGE,
  COMPONENT_MESSAGE_PARAMETERS,
  ComponentMessagingService,
  CoreService,
  DialogService,
  FormNavigationService,
  SpinnerService
} from 'dspx-core';

import { API_ENDPOINT_KEYS, API_ENDPOINTS } from '../../constants/api-endpoints.constants';
import { TEMPLATES, VERSIONS } from '../../constants/templates.constants';
import { FORM_DUMMY_IDS, OPTION_SETS, POP_UP_CODES, QUESTION_PATHS, SAVE_RESULT_STATUS, UiDateFormat } from '../../constants/app-data.constants';

import { InspectionCalendarFilterRequest } from '../../interfaces/inspection-calendar-filter-request.interface';

import { DateTimeHelper } from '../../helpers/date-time-helper';
import { DateTimeValidator } from '../../helpers/date-time-validator';

import { HtmlHelperService } from '../../services/html-helper.service';
import { ServiceWorkerService } from '../../services/service-worker.service';
import { AppDataService } from '../../services/app-data.service';
import { ExcelImportService } from '../../services/excel-import.service';
import { ResponseParserService } from '../../services/response-parser.service';
import { ErrorService } from '../../services/error.service';

import { BaseCardSetDetailComponent } from '../base-card-set-detail-component/base-card-set-detail.component';

@Component({
  selector: 'app-inspection-calendar',
  templateUrl: './inspection-calendar.component.html',
  styleUrls: ['./inspection-calendar.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class InspectionCalendarComponent extends BaseCardSetDetailComponent implements OnInit, OnDestroy {

  private activeDialog = '';
  private eventInfo: DateSelectArg | EventClickArg;

  formServiceEndpoint: string = API_ENDPOINT_KEYS.getInspectionCalendarConfigAndResponse;
  templateId: number = TEMPLATES.inspectionCalendar;
  versionNo: number = VERSIONS.latest;
  id: string = FORM_DUMMY_IDS.inspectionCalendar.id;
  inspectionEvent: any;
  inspectionSaveStatus: number;

  constructor(
    protected route: ActivatedRoute,
    protected router: Router,
    protected serviceWorkerService: ServiceWorkerService,
    protected appDataService: AppDataService,
    protected spinnerService: SpinnerService,
    protected dialogService: DialogService,
    protected excelImportService: ExcelImportService,
    protected coreService: CoreService,
    protected htmlHelperService: HtmlHelperService,
    protected responseParserService: ResponseParserService,
    protected formNavigationService: FormNavigationService,
    private componentMessagingService: ComponentMessagingService,
    private errorService: ErrorService) {

    super(
      router,
      serviceWorkerService,
      appDataService,
      spinnerService,
      htmlHelperService,
      dialogService,
      responseParserService,
      formNavigationService);
  }

  ngOnInit(): void {

    const logPrefix = 'ngOnInit';
    this.logger.log(`${logPrefix}: enter`);

    this.ignoreCanDeactivateGuard = true;

    this.coreService.applicationFunctions['validateScheduleDate'] = (args: any[]) =>
      DateTimeValidator.validateDateFormat(args) &&
      DateTimeValidator.validateScheduleDate(args) &&
      DateTimeValidator.validateScheduleDateTooFarAhead(args);

    this.overrideHtmlTitleSetByDspxFrameworkOnFormNavigation();
    this.waitForServiceWorker();

    this.componentMessagingService.currentMessage$.subscribe(
      (componentMessage) => {
        switch (componentMessage.message) {
          case COMPONENT_MESSAGE.calendarEventSelected:
            this.activeDialog = POP_UP_CODES.inspectionCalendar.modifyCalendarAppointment;
            this.eventInfo = componentMessage.parameters?.[COMPONENT_MESSAGE_PARAMETERS.calendarEventInfo] as EventClickArg;
            
            // the inspection event from the model is the source of truth over the component message
            const inspectionEventFromModel = this.getInspectionEventFromModel(this.eventInfo.event.id);

            this.inspectionEvent = this.eventInfo.event.extendedProps;
            this.inspectionEvent =
            {
              ...this.inspectionEvent,
              targetedInspectionDateAmPm: inspectionEventFromModel?.targetedInspectionDateAmPm?.toLowerCase(),
              targetedInspectionDate: inspectionEventFromModel?.targetedInspectionDate,
              inspectionPeriodEndDate: inspectionEventFromModel?.inspectionPeriodEndDate,
              bookingConfirmed: this.getBookingConfirmedOptionCode(this.inspectionEvent?.isBookingConfirmed),
              inspectorNote: inspectionEventFromModel?.inspectorNote,
              eventStartTime: {
                hours: this.eventInfo.event.start ? this.eventInfo.event.start.getHours() : 0,
                minutes: this.eventInfo.event.start ? this.eventInfo.event.start.getMinutes() : 0
              }
            };

            this.dialogService.showPopup(
              POP_UP_CODES.inspectionCalendar.modifyCalendarAppointment,
              this.inspectionEvent,
              this.inspectionEvent
            );
            break;
          case COMPONENT_MESSAGE.calendarEditEventCompleted:
            // update the calendar model to reflect the changes
            const response = componentMessage.parameters?.[COMPONENT_MESSAGE_PARAMETERS.calendarApiResponse];
            const resultModel = {
              ...response.saveAppointmentResult,
              pin: response.pin 
            };

            // store the inspection save status for use in modifyCalendarAppointmentResult dialog button click handling
            this.inspectionSaveStatus = response.saveAppointmentResult.inspectionSaveStatus;
            this.inspectionEvent = {
              ...this.getInspectionEventFromModel(response.id),
              targetedInspectionDate: response.saveAppointmentRequest.appointmentDate,
              targetedInspectionDateAmPm: response.saveAppointmentRequest.targetedInspectionDateAmPm,
              inspectionPeriodEndDate: response.saveAppointmentRequest.inspectionPeriodEndDate,
              lastUpdatedTime: response.saveAppointmentResult.allocatedLastUpdatedTime,
              bookingConfirmed: this.getBookingConfirmedOptionCode(response.saveAppointmentRequest.isBookingConfirmed),
              eventTypeCode: this.getEventTypeCode(response.saveAppointmentRequest.isBookingConfirmed),
              inspectorNote: response.saveAppointmentRequest.inspectorNote
            };
            this.openDialogForResult(resultModel, POP_UP_CODES.inspectionCalendar.modifyCalendarAppointmentResult);
            break;
          default:
            break;
        }
      }
    );

    this.formServiceEndpoint = API_ENDPOINT_KEYS.getInspectionCalendarConfigAndResponse;

    this.logger.log(`${logPrefix}: leave`);
  }

  ngOnDestroy(): void {
    this.completeDestroy();
  }

  formLoad(event: any): void {
  }

  filterData(event: any): void {
    this.loadFilteredInspectionCalendarData();
  }

  dialogButton_modifyCalendarAppointment_Clicked(event: any): void {
    this.saveAppointment(event, POP_UP_CODES.inspectionCalendar.modifyCalendarAppointmentResult);
  }

  dialogButton_modifyCalendarAppointmentResult_Clicked(event: any): void {
    if(this.inspectionSaveStatus === SAVE_RESULT_STATUS.inspection.success) {
      this.updateInspectionEventModel(
        this.inspectionEvent.inspectionId,
        this.inspectionEvent.targetedInspectionDate,
        this.inspectionEvent.targetedInspectionDateAmPm,
        this.inspectionEvent.inspectionPeriodEndDate,
        this.inspectionEvent.lastUpdatedTime,
        this.inspectionEvent.inspectionDateType,
        this.inspectionEvent.isBookingConfirmed,
        this.inspectionEvent.inspectorNote
      );
    }
    else {
      // if not successful reload the calendar control to show previous model
      this.loadFilteredInspectionCalendarData();
    }
  }

  private loadFilteredInspectionCalendarData() {
    const filterRequest = this.getFilterRequest();
    
    this.spinnerService.showSpinner();
    this.appDataService.getData(
      API_ENDPOINTS.inspectionCalendar.getInspectionCalendarEvents,
      filterRequest
    )
      .pipe(take(1),
        takeUntil(this.destroy$),
        this.errorService.errorHandler(),
        finalize(() => this.spinnerService.hideSpinner())
      )
      .subscribe(
        (inspectionCalendarEvents: any) => {
          // update the calendar with the new events
          this.componentMessagingService.sendMessage(
            {
              message: COMPONENT_MESSAGE.calendarSetEvents,
              parameters: {
                events: inspectionCalendarEvents
              }
            }
          );
        }
      );
  }

  private saveAppointment(inspection: any, popupCode: string): void {
    const getAppointmentDate = (dateString: string, eventStartTime: { hours: number, minutes: number }): string => {
      if (!dateString) {
        return null;
      }
      
      return DateTimeHelper.getIsoDateString(dateString, UiDateFormat, eventStartTime.hours, eventStartTime.minutes);
    };
    
    const appointmentDate = getAppointmentDate(inspection.targetedInspectionDate, this.inspectionEvent.eventStartTime);
    const inspectionPeriodEndDate = getAppointmentDate(inspection.inspectionPeriodEndDate, this.inspectionEvent.eventStartTime);

    this.postInspectionAction(
      API_ENDPOINTS.inspectionCalendar.saveAppointment,
      {
        id: inspection.inspectionId,
        template: inspection.template,
        response: inspection.response,
        inspectionId: inspection.inspectionId,
        appointmentDate: appointmentDate,
        targetedInspectionDateAmPm: inspection.targetedInspectionDateAmPm,
        inspectionPeriodEndDate: inspectionPeriodEndDate,
        pin: inspection.pin,
        isCancelled: inspection.isCancelled,
        lastUpdatedTime: inspection.lastUpdatedTime,
        inspectionDateType: inspection.inspectionDateType,
        isBookingConfirmed: inspection.bookingConfirmed ? inspection.bookingConfirmed == OPTION_SETS.yesNo.yes ? true : false : null,
        inspectorNote: inspection.inspectorNote
      },
      popupCode
    );
  }

  private postInspectionAction(
    apiEndPoint: string,
    payload: any,
    resultPopupCode: string
  ): void {
    this.spinnerService.showSpinner();
    this.appDataService.post(apiEndPoint, payload)
      .pipe(take(1),
        takeUntil(this.destroy$),
        this.errorService.errorHandler(),
        finalize(() => this.spinnerService.hideSpinner())
      )
      .subscribe(data => {
        if (resultPopupCode) {
          if (apiEndPoint === API_ENDPOINTS.inspectionCalendar.saveAppointment) {
            this.inspectionSaveStatus = data.inspectionSaveStatus;

            if (data.inspectionSaveStatus === SAVE_RESULT_STATUS.inspection.success) {
              this.inspectionEvent.lastUpdatedTime = data.allocatedLastUpdatedTime;
              this.inspectionEvent.targetedInspectionDate = payload.appointmentDate;
              this.inspectionEvent.targetedInspectionDateAmPm = payload.targetedInspectionDateAmPm;
              this.inspectionEvent.inspectionPeriodEndDate = payload.inspectionPeriodEndDate;
              this.inspectionEvent.inspectionDateType = payload.inspectionDateType;
              this.inspectionEvent.isBookingConfirmed = payload.isBookingConfirmed;
              this.inspectionEvent.eventTypeCode = this.getEventTypeCode(payload.isBookingConfirmed);
              this.inspectionEvent.inspectorNote = payload.inspectorNote;
              this.openDialogForResult(data, resultPopupCode);
            }
          }
          else {
            this.openDialogForResult(data, resultPopupCode);
          }
        }
      });
  }

  private openDialogForResult(model: any, popupCode: string): void {
    this.activeDialog = popupCode;
    let messagePlaceholders: any;
    
    const messageWithValues = (model?.details?.[POP_UP_CODES.resultPopupMessageKey] || '').replace('{{pin}}', this.inspectionEvent?.pin ?? model?.pin);

    switch(popupCode) {
      case POP_UP_CODES.inspectionCalendar.modifyCalendarAppointmentResult:
        messagePlaceholders = { modifyCalendarAppointmentResultMessage: messageWithValues };
        break;
      default:
        messagePlaceholders = {};
        console.error('Unknown popup code:', popupCode);
    }
    this.dialogService.showPopup(this.activeDialog, {}, messagePlaceholders);
  }

  private getInspectionEventFromModel(inspectionEventId?: string): any {
    const inspectionEventsFromModel = this.getQuestionValue(this.baseForm?.model, ...QUESTION_PATHS.inspectionCalendar.calendar);
    const inspectionEventFromModel = inspectionEventsFromModel?.events?.find(event => event.id == inspectionEventId)?.extendedProps;
    return inspectionEventFromModel;
  }

  private updateInspectionEventModel(
    inspectionid: number,
    targetedInspectionDate: string,
    targetedInspectionDateAmPm: string,
    inspectionPeriodEndDate: string,
    allocatedLastUpdatedTime: string,
    inspectionDateType: string,
    isBookingConfirmed?: boolean,
    inspectorNote?: string
  ): void {
    const inspectionCalendarModel = this.getQuestionValue(this.baseForm?.model, ...QUESTION_PATHS.inspectionCalendar.calendar);
    if (inspectionCalendarModel) {
      // replace the part of the model that contains the inspection event.
      const inspectionEvent = inspectionCalendarModel.events?.find(event => event.id == inspectionid);
      if (inspectionEvent) {
        inspectionEvent.extendedProps.targetedInspectionDate = DateTimeHelper.formatToUiDate(targetedInspectionDate);
        inspectionEvent.extendedProps.targetedInspectionDateAmPm = targetedInspectionDateAmPm;
        inspectionEvent.extendedProps.inspectionPeriodEndDate = DateTimeHelper.formatToUiDate(inspectionPeriodEndDate);
        inspectionEvent.extendedProps.lastUpdatedTime = allocatedLastUpdatedTime;
        inspectionEvent.start = targetedInspectionDate ? targetedInspectionDate : inspectionPeriodEndDate;
        // all calendar events are 1 hour long
        inspectionEvent.end = DateTimeHelper.addHoursToIsoDateString(inspectionEvent.start, 1);
        inspectionEvent.extendedProps.inspectionDateType = inspectionDateType;
        inspectionEvent.extendedProps.isBookingConfirmed = isBookingConfirmed;
        inspectionEvent.extendedProps.eventTypeCode = this.getEventTypeCode(isBookingConfirmed);
        inspectionEvent.extendedProps.inspectorNote = inspectorNote;

        this.componentMessagingService.sendMessage(
          {
            message: COMPONENT_MESSAGE.calendarSetEvents,
            parameters: {
              events: inspectionCalendarModel.events
            }
          }
        );
      }
    }
  }

  private getBookingConfirmedOptionCode(isBookingConfirmed: boolean | null): number | null {
    return isBookingConfirmed === null || isBookingConfirmed === undefined ?
      null :
      isBookingConfirmed ? OPTION_SETS.yesNo.yes : OPTION_SETS.yesNo.no;
  }

  private getEventTypeCode(isBookingConfirmed: boolean | null): string {
    return isBookingConfirmed ?
      OPTION_SETS.calendarEventType.inspectionClientContacted :
      OPTION_SETS.calendarEventType.inspection;
  }

  private getFilterRequest(): InspectionCalendarFilterRequest {
    const filterQuestionModel = this.getQuestionValue(this.baseForm?.model, ...QUESTION_PATHS.inspectionCalendar.filter);

    return {
      assignedTo: filterQuestionModel?.assignedTo,
    };
  }
}
